import { ThemeEvents } from '@theme/events';

class DroplandProfitCalculator extends HTMLElement {
  connectedCallback() {
    this.variantPrice = Number(this.dataset.cost || 0);
    this.quantityInput = this.querySelector('[data-calculator-quantity]');
    this.saleInput = this.querySelector('[data-calculator-sale]');
    this.minusButton = this.querySelector('[data-calculator-minus]');
    this.plusButton = this.querySelector('[data-calculator-plus]');
    this.setQuantity(this.mainQuantity(), false);
    this.saleInput?.addEventListener('input', () => this.render());
    this.quantityInput?.addEventListener('input', () => this.setQuantity(this.quantityInput.value));
    this.quantityInput?.addEventListener('change', () => this.setQuantity(this.quantityInput.value));
    this.minusButton?.addEventListener('click', () => this.setQuantity(this.quantity - 1));
    this.plusButton?.addEventListener('click', () => this.setQuantity(this.quantity + 1));
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onMainQuantityUpdate);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.onMainQuantityUpdate);
  }

  onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    const price = Number(variant?.price);
    if (Number.isFinite(price)) {
      this.variantPrice = price;
      this.render();
    }
  };

  onMainQuantityUpdate = (event) => {
    const selector = event.target;
    if (!(selector instanceof HTMLElement) || selector !== this.mainQuantitySelector()) return;
    this.setQuantity(event.detail?.quantity, false);
  };

  get quantity() {
    return Math.max(1, Number.parseInt(this.quantityInput?.value, 10) || 1);
  }

  mainQuantitySelector() {
    const form = document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`)
      || document.querySelector('product-form-component');
    return form?.querySelector('quantity-selector-component') || null;
  }

  mainQuantity() {
    const input = this.mainQuantitySelector()?.querySelector('input[name="quantity"]');
    return input?.value || 1;
  }

  tiers() {
    const productSection = this.mainQuantitySelector()?.closest('.shopify-section');
    const entries = [...(productSection || document).querySelectorAll('.dropland-wholesale-pricing__tier[data-tier][data-price-cents]')]
      .map((tier) => ({ minimum: Number.parseInt(tier.dataset.tier, 10), price: Number(tier.dataset.priceCents) }))
      .filter((tier) => Number.isFinite(tier.minimum) && Number.isFinite(tier.price))
      .sort((a, b) => a.minimum - b.minimum);

    return entries.length ? entries : [{ minimum: 1, price: this.variantPrice }];
  }

  currentTier() {
    const entries = this.tiers();
    return entries.filter((tier) => tier.minimum <= this.quantity).at(-1) || entries[0];
  }

  setQuantity(value, syncMain = true) {
    const selector = this.mainQuantitySelector();
    const mainInput = selector?.querySelector('input[name="quantity"]');
    const minimum = Number.parseInt(mainInput?.min, 10) || 1;
    const maximum = Number.parseInt(mainInput?.max, 10);
    const next = Math.max(minimum, Math.min(Number.isFinite(maximum) ? maximum : Infinity, Number.parseInt(value, 10) || minimum));

    if (this.quantityInput) this.quantityInput.value = String(next);
    if (syncMain && mainInput && Number.parseInt(mainInput.value, 10) !== next) {
      mainInput.value = String(next);
      selector?.onQuantityChange?.();
      selector?.updateButtonStates?.();
    }
    this.render();
  }

  format(value) {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: this.dataset.currency || 'MXN', maximumFractionDigits: 0 }).format(value);
  }

  render() {
    const sale = Math.max(0, Number(this.saleInput?.value || 0));
    const tier = this.currentTier();
    const unitCost = tier.price / 100;
    const profit = sale - unitCost;
    const investment = unitCost * this.quantity;
    const totalProfit = profit * this.quantity;
    const costNode = this.querySelector('[data-calculator-cost]');
    const profitNode = this.querySelector('[data-calculator-profit]');
    if (costNode) costNode.textContent = this.format(unitCost);
    if (profitNode) profitNode.textContent = this.format(profit);
    const tierText = this.querySelector('[data-calculator-tier]');
    const investmentNode = this.querySelector('[data-calculator-investment]');
    const investmentDetail = this.querySelector('[data-calculator-investment-detail]');
    const totalProfitNode = this.querySelector('[data-calculator-total-profit]');
    if (tierText) {
      const pieceLabel = this.quantity === 1 ? this.dataset.pieceSingular : this.dataset.piecePlural;
      tierText.textContent = `${this.dataset.tierPrefix} ${this.quantity} ${pieceLabel}`;
    }
    if (investmentNode) investmentNode.textContent = this.format(investment);
    if (investmentDetail) investmentDetail.textContent = `${this.format(unitCost)} × ${this.quantity}`;
    if (totalProfitNode) totalProfitNode.textContent = `${this.dataset.totalProfitPrefix} ${this.format(totalProfit)}`;
  }
}

if (!customElements.get('dropland-profit-calculator')) {
  customElements.define('dropland-profit-calculator', DroplandProfitCalculator);
}
